// First-sign-in gate: a signed-in person who has not accepted the bundled
// Terms of Use sees nothing else until they do. Sign-in itself is the Cognito
// hosted page, which cannot carry a checkbox, so the affirmative act lives
// here, right after it, and is recorded per person and per Terms version.
import { useState, type ReactNode } from 'react';
import { useAuth } from 'react-oidc-context';
import { useQueryClient } from '@tanstack/react-query';
import { acceptTerms } from '@/lib/api';
import { beginSignOut, cognitoLogoutUrl } from '@/lib/auth-config';
import { TERMS_VERSION, needsTermsAcceptance } from '@/lib/terms';
import { useMe } from '@/lib/useMe';
import { buttonVariants } from '@/components/ui/Button';

export function RequireTerms({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const me = useMe();
  if (!needsTermsAcceptance(me.data, auth.isAuthenticated)) return <>{children}</>;
  return <TermsGate />;
}

function TermsGate() {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const accept = async () => {
    const token = auth.user?.id_token;
    if (!token) return;
    setBusy(true);
    setError(null);
    const r = await acceptTerms(TERMS_VERSION, token);
    if (r.ok) {
      await queryClient.invalidateQueries({ queryKey: ['me'] });
    } else {
      setError(r.message);
    }
    setBusy(false);
  };

  const signOut = async () => {
    beginSignOut();
    await auth.removeUser();
    window.location.href = cognitoLogoutUrl();
  };

  return (
    <div className="container mx-auto flex justify-center py-12">
      <section className="w-full max-w-lg rounded-lg border border-border bg-card p-6 shadow-sm">
        <h1 className="text-lg font-semibold text-tres-text-primary">Before you begin</h1>
        <p className="mt-2 text-sm text-tres-text-secondary">
          Use of this TReS environment is governed by the Terms of Use, revised {TERMS_VERSION}.
          Please read them, then confirm below. You will be asked again only if they change.
        </p>
        <p className="mt-3 text-sm">
          <a
            href="/terms"
            target="_blank"
            rel="noopener"
            className="font-medium text-tres-action hover:underline"
          >
            Read the Terms of Use
          </a>
        </p>
        <label className="mt-5 flex items-start gap-3 text-sm text-tres-text-primary">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-0.5 h-4 w-4"
          />
          <span>I have read the Terms of Use and agree to them on behalf of myself and the organisation I represent.</span>
        </label>
        {error && (
          <p className="mt-3 text-sm text-destructive" role="alert">
            Your acceptance could not be recorded: {error}
          </p>
        )}
        <div className="mt-6 flex items-center justify-between">
          <button
            type="button"
            onClick={() => void signOut()}
            className={buttonVariants({ variant: 'ghost', size: 'icon' })}
          >
            Sign out
          </button>
          <button
            type="button"
            disabled={!agreed || busy}
            onClick={() => void accept()}
            className={buttonVariants({ variant: 'primary', size: 'lg' })}
          >
            {busy ? 'Recording…' : 'Accept and continue'}
          </button>
        </div>
      </section>
    </div>
  );
}
